import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Loader2 } from "lucide-react";
import { teamsAPI } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface AddMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMemberAdded: () => void;
  teamId: string;
  teamName: string;
}

export const AddMemberModal = ({ 
  open, 
  onOpenChange, 
  onMemberAdded, 
  teamId,
  teamName 
}: AddMemberModalProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!teamId) {
      toast({
        title: t("common.error"),
        description: "No team selected",
        variant: "destructive",
      });
      return;
    }
    
    if (!email.trim() || !name.trim() || !reason.trim()) {
      toast({
        title: t("common.error"),
        description: t("addMember.fieldsRequired"),
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: t("common.error"),
        description: t("addMember.invalidEmail"),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const response = await teamsAPI.addMember(teamId, {
        email: email.trim(),
        name: name.trim(),
        role,
        reason: reason.trim(),
      });

      if (response.success) {
        toast({
          title: t("addMember.successTitle"),
          description: t("addMember.successDesc", { name, teamName }),
        });

        // Reset form
        setEmail("");
        setName("");
        setRole("member");
        setReason("");
        onOpenChange(false);
        onMemberAdded();
      } else {
        throw new Error(response.message || 'Failed to add member');
      }

    } catch (error: any) {
      console.error("Error adding member:", error);
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "addMember.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-card border-border">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-primary" />
            </div>
            <DialogTitle className="text-2xl">
              {t("addMember.title")}
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            {teamName ? t("addMember.description", { teamName }) : "Add a member to the team"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="memberEmail" className="text-foreground font-semibold">
                {t("addMember.email")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="memberEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("addMember.emailPlaceholder")}
                className="bg-background border-border/50 focus:border-primary"
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="memberName" className="text-foreground font-semibold">
                {t("addMember.name")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="memberName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("addMember.namePlaceholder")}
                className="bg-background border-border/50 focus:border-primary"
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="memberRole" className="text-foreground font-semibold">
                {t("addMember.role")} <span className="text-destructive">*</span>
              </Label>
              <Select value={role} onValueChange={(value: "member" | "admin") => setRole(value)} disabled={loading}>
                <SelectTrigger className="bg-background border-border/50 focus:border-primary">
                  <SelectValue placeholder={t("addMember.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">{t("addMember.roleMember")}</SelectItem>
                  <SelectItem value="admin">{t("addMember.roleAdmin")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {role === "admin" 
                  ? t("addMember.adminDescription")
                  : t("addMember.memberDescription")
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="memberReason" className="text-foreground font-semibold">
                {t("addMember.reason")} <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="memberReason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("addMember.reasonPlaceholder")}
                className="w-full min-h-[80px] rounded-md border border-border/50 bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading}
                required
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 shadow-glow"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("addMember.adding")}
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t("addMember.addButton")}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
